import { isValidElement } from "react";
import classNames from "classnames";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { Badge } from "./Badge";
import styles from "./BadgeCore.module.scss";

const BadgeSlice = ({ color, className }: { color: string; className?: string }) => (
	<svg
		width="44"
		height="24"
		viewBox="0 0 44 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		className={className}
	>
		<path
			d="M20.6515 0L7.76048 18.4583C6.07904 20.8881 3.31976 23.7869 0 24H23C26.6862 24 28.9102 21.5702 30.7641 18.8419L44 0H20.6515Z"
			fill={color}
		/>
	</svg>
);

export type BadgeCoreProps = {
	color?: string;
	accentColor?: string;
	icon?: React.ReactNode;
	primaryText?: string;
	secondaryText?: string;
	showIcon?: boolean;
	showPrimaryText?: boolean;
	showSecondaryText?: boolean;
	variant?: "shadow" | "outline";
	className?: string;
};

export const BadgeCore = ({
	color = "var(--color-black, $black)",
	accentColor = "var(--color-black, $black)",
	icon,
	primaryText,
	secondaryText,
	showIcon = true,
	showPrimaryText = true,
	showSecondaryText = true,
	variant = "shadow",
	className,
	...props
}: BadgeCoreProps) => {
	const hasIcon = showIcon && isValidElement(icon);
	const hasPrimary = Boolean(showPrimaryText && primaryText);
	const hasSecondary = Boolean(showSecondaryText && secondaryText);

	return (
		<Badge variant={variant} className={className} {...props}>
			{(hasIcon || hasPrimary) && (
				<span
					style={{ color: accentColor }}
					className={classNames(styles["badge-core__primary"], {
						[styles["badge-core__primary--standalone-text"]]:
							hasPrimary && !hasIcon && !hasSecondary,

						[styles["badge-core__primary--standalone-icon-text"]]:
							hasPrimary && hasIcon && !hasSecondary,

						[styles["badge-core__primary--standalone-icon"]]:
							!hasPrimary && hasIcon && !hasSecondary,

						[styles["badge-core__primary--before-secondary"]]: hasSecondary,
					})}
				>
					{hasIcon && icon}

					{hasPrimary && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.SMALL}
							isBold
							component="span"
							className={styles["badge-core__primary-text"]}
							style={{ color }}
						>
							{primaryText}
						</Typography>
					)}
				</span>
			)}

			{hasSecondary && (
				<span
					className={styles["badge-core__secondary"]}
					style={{ backgroundColor: accentColor }}
				>
					{(hasPrimary || hasIcon) && (
						<BadgeSlice color={accentColor} className={styles["badge-core__slice"]} />
					)}

					<Typography
						variant={TYPOGRAPHY_VARIANTS.SMALL}
						className={styles["badge-core__secondary-text"]}
						isBold
						component="span"
					>
						{secondaryText}
					</Typography>
				</span>
			)}
		</Badge>
	);
};
